import { Injectable } from '@angular/core';
import { SelectedItem, ToInventoryItem } from '@galaxy/inventory-ui';
import { App, AppPrices, FieldMask } from '@vendasta/marketplace-apps';
import { AppPartnerService, AppPricingService } from '@galaxy/marketplace-apps';
import { MarketplacePackagesApiService, Package } from '@vendasta/marketplace-packages';
import { convertItemsToAppKeys, mapify } from './utils';
import { combineLatest, map, Observable, of } from 'rxjs';
import { getPartnerCurrency } from '@vendasta/billing-ui';
import { convertAppPriceToRevenuePrice } from '../../conversion-utils';
import { AppWithRetailPricing } from '../sales-order-line-item-selector.component';

@Injectable()
export class ItemSelectorAdapterService {
  constructor(
    private appPartnerService: AppPartnerService,
    private appPricingService: AppPricingService,
    private readonly marketplacePackagesApiService: MarketplacePackagesApiService,
  ) {}

  public static convertAppsToItems(apps: App[]): SelectedItem[] {
    return apps.map((a) => ToInventoryItem<App>(a));
  }

  public static convertAppsAndPackagesToItems(apps: App[], packages: Package[]): SelectedItem[] {
    const selectedItems = apps.map((a) => ToInventoryItem<App>(a));
    selectedItems.push(...packages.map((p) => ToInventoryItem<Package>(p)));
    return selectedItems;
  }

  public getMarketplaceApps(items: SelectedItem[], partnerId: string, marketId: string): Observable<Map<string, App>> {
    const appItems = items.filter((item) => item.type === 'app' || item.type === 'addon');
    if (!appItems.length) return of(new Map<string, App>());

    return this.appPartnerService
      .getMulti(convertItemsToAppKeys(appItems), partnerId, marketId, null, true)
      .pipe(map((apps) => mapify(apps, (app: App) => app.key.appId)));
  }

  public getMarketplacePackages(items: SelectedItem[]): Observable<Package[]> {
    const packageIds = items.filter((item) => item.type === 'package').map((item) => item.itemId);
    if (!packageIds.length) return of([]);

    return this.marketplacePackagesApiService
      .getMultiPackages({ packageIds: packageIds })
      .pipe(map((resp) => resp.packageContainers.map((container) => container.package)));
  }

  public getRetailPricing(appIds: string[], partnerId: string, marketId: string): Observable<Map<string, AppPrices>> {
    return this.appPricingService
      .getMultiPricing(appIds, new FieldMask({ paths: ['retail'] }), partnerId, marketId)
      .pipe(map((appPrices) => mapify(appPrices, (ap: AppPrices) => ap.appId)));
  }

  public getAppsWithRetailPricing(
    selectedItems: SelectedItem[],
    partnerId: string,
    marketId: string,
  ): Observable<AppWithRetailPricing[]> {
    const items = selectedItems.filter((item) => item.type === 'app' || item.type === 'addon');
    if (!items.length) {
      return of([]);
    }

    const itemIds: string[] = items.map((item) => item.itemId);
    return combineLatest([
      this.getMarketplaceApps(items, partnerId, marketId),
      this.getRetailPricing(itemIds, partnerId, marketId),
    ]).pipe(
      map(([apps, appPrices]) =>
        items.map((item) => {
          const app = apps.get(item.itemId);
          const appPrice = appPrices.get(item.itemId);
          const partnerCurrency = getPartnerCurrency(appPrice.pricesForContexts['retail']);
          const appWithRetailPrice = app as AppWithRetailPricing;
          appWithRetailPrice.retailPricing = {
            currencyCode: partnerCurrency,
            revenue: convertAppPriceToRevenuePrice(
              appPrice.pricesForContexts['retail'],
              partnerCurrency,
              app.key.editionId ?? '',
            ),
          };
          return appWithRetailPrice;
        }),
      ),
    );
  }
}
